import React, { useRef, useState, useEffect, Children } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { Box, IconButton, Typography, useMediaQuery } from '@mui/material'
import FilterAltIcon from '@mui/icons-material/FilterAlt'

import AdxDropdown from 'components/custom/AdxDropdown'
import AdxFormControlLabel from 'components/custom/AdxFormControlLabel'
import AdxCheckbox from 'components/custom/AdxCheckbox'
import AdxPopper from 'components/reusable/AdxPopper'
import CalendarApiFilter from './calendarApiFilter'
import { profileDepartments } from 'services/api/dropdown'
import AdxButton from 'components/custom/AdxButton'

const getFilterText = (key) => {
  switch (key) {
    case 'holiday':
      return 'Company & public holiday'
    case 'leave':
      return 'Staff on leave'
    case 'birthday':
      return "Staff's birthday"
    case 'anniversary':
      return "Staff's work anniversary"

    default:
      return null
  }
}
const getFilterColor = (key) => {
  switch (key) {
    case 'holiday':
      return 'information.main'
    case 'leave':
      return 'error.main'
    case 'birthday':
      return 'warning.main'
    case 'anniversary':
      return 'positive.main'

    default:
      return null
  }
}

const CalendarFilter = ({ filters, setFilters, apiFilter, setApiFilter }) => {
  const filterContainerRef = useRef(null)
  const [filterContainerWidth, setFilterContainerWidth] = useState()
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down('sm'))
  const { data: departmentList } = useQuery('departments-dropdown', async () => {
    const { data } = await profileDepartments()
    return data
  })

  useEffect(() => {
    if (filterContainerRef.current) {
      setFilterContainerWidth(filterContainerRef.current.clientWidth)
    }
  }, [isSmall])

  return (
    <Box
      ref={filterContainerRef}
      sx={{
        display: 'flex',
        gap: '20px',
      }}
    >
      <AdxPopper
        customButton={
          <IconButton>
            <FilterAltIcon />
          </IconButton>
        }
        sx={{
          width: filterContainerWidth,
          maxHeight: '550px',
          p: 0,
          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
        }}
        childBoxSx={{ m: 0, p: 0 }}
        placement="bottom-start"
        closeOnClick="button"
      >
        <MyBox apiFilter={apiFilter} setApiFilter={setApiFilter}>
          <CalendarApiFilter
            title="Departments"
            searchString="department"
            apiData={departmentList}
          />
        </MyBox>
      </AdxPopper>
      <AdxDropdown
        sx={{ width: '320px' }}
        sxDropdown={{
          border: '1px solid #E5E5E5',
          backgroundColor: 'white.main',
          outline: 'none',
          borderRadius: 1.25,
          '&.Mui-focused': {
            '& .MuiOutlinedInput-notchedOutline': {
              borderWidth: 0,
            },
            outline: 'none',
          },
        }}
        menuPropsSx={{
          '.MuiPaper-root': {
            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
            border: '1px solid #e5e5e5',
          },
        }}
        multiple
        displayEmpty
        value={Object.entries(filters).filter(([k, val]) => {
          if (val) {
            return k
          } else {
            return null
          }
        })}
        renderValue={(option) => {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                {option.map((item) => (
                  <Box
                    key={item[0]}
                    width="2px"
                    height="20px"
                    borderRadius={1}
                    bgcolor={getFilterColor(item[0])}
                  />
                ))}
              </Box>
              <Typography variant="caption" textTransform="capitalize">
                {option.length === 4
                  ? 'Showing all'
                  : option.length
                  ? option.map((item, index) => {
                      if (index === option.length - 1) {
                        return item[0]
                      }
                      return `${item[0]}, `
                    })
                  : 'None selected'}
              </Typography>
            </Box>
          )
        }}
      >
        <Box
          width={1}
          px={1.5}
          value={
            Object.entries(filters).filter(([k, val]) => {
              if (val) {
                return k
              } else {
                return null
              }
            })[0]
          }
        >
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {Object.keys(filters).map((k) => (
              <AdxFormControlLabel
                key={k}
                label={getFilterText(k)}
                control={
                  <AdxCheckbox
                    checked={filters[k]}
                    name={k}
                    onChange={(event) => {
                      setFilters({
                        ...filters,
                        [event.target.name]: event.target.checked,
                      })
                    }}
                    sx={{
                      color: getFilterColor(k),
                      '&.Mui-checked': {
                        color: getFilterColor(k),
                      },
                    }}
                  />
                }
              />
            ))}
          </Box>
        </Box>
      </AdxDropdown>
    </Box>
  )
}

export default CalendarFilter

const MyBox = ({ children, apiFilter, setApiFilter, ...rest }) => {
  const queryClient = useQueryClient()

  const [data, setData] = useState(
    Children.map(children, (child) => ({
      name: child.props.title,
      searchString: child.props.searchString,
      filteredData:
        apiFilter.length > 0
          ? apiFilter.find((data) => data.name === child.props.title).filteredData
          : child.props.apiData.map((data) => data.id),
    }))
  )

  const setSingleData = (name, searchString, arr) => {
    const index = data.findIndex((data) => data.name === name)
    if (index > -1) {
      setData((prevState) =>
        prevState.map((data, idx) =>
          index === idx ? { name: name, searchString, filteredData: arr } : data
        )
      )
    }
  }

  const childrenWithProps = Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, {
        data: data.find((data) => data.name === child.props.title).filteredData,
        setData: setSingleData,
      })
    }
    return child
  })
  return (
    <Box {...rest}>
      <Box sx={{ maxHeight: '450px', overflow: 'auto', px: 2, py: 1 }}>{childrenWithProps}</Box>
      <Box sx={{ p: 2 }}>
        <AdxButton
          variant={'contained'}
          onClick={() => {
            setApiFilter(data)
            queryClient.removeQueries('big-calendar')
          }}
          sx={{ p: 1.5 }}
          fullWidth
        >
          Apply Filter
        </AdxButton>
      </Box>
    </Box>
  )
}
